import SEO from "@/components/SEO";
import AvatarGenerator from "@/components/AvatarGenerator";
import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/pages/home/components/Footer";

const LOGO_URL = "https://pub-bc092def332d4fb180f02b510802da8e.r2.dev/LogoGP.png";

function calcularIdade(dataNasc: string): number | null {
  if (!dataNasc) return null;
  const nasc = new Date(dataNasc);
  const hoje = new Date();
  let idade = hoje.getFullYear() - nasc.getFullYear();
  const m = hoje.getMonth() - nasc.getMonth();
  if (m < 0 || (m === 0 && hoje.getDate() < nasc.getDate())) idade--;
  return idade;
}

function formatCpf(v: string) {
  const digits = v.replace(/\D/g, "").slice(0, 11);
  return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
}

function formatPhone(v: string) {
  const digits = v.replace(/\D/g, "").slice(0, 11);
  if (digits.length > 6) return digits.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
  if (digits.length > 2) return digits.replace(/(\d{2})(\d*)/, "($1) $2");
  return digits;
}

function formatCpfDisplay(v: string) {
  const digits = v.replace(/\D/g, "").slice(0, 11);
  if (digits.length === 11) return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  return digits;
}

function formatPhoneDisplay(v: string) {
  const digits = v.replace(/\D/g, "").slice(0, 11);
  if (digits.length === 11) return digits.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
  if (digits.length === 10) return digits.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
  return digits;
}

function formatDateBR(v: string) {
  if (!v) return "";
  // Força interpretação como data local (sem conversão de timezone)
  const [year, month, day] = v.split("-");
  if (!year || !month || !day) return v;
  return `${day}/${month}/${year}`;
}

export default function MeuPerfilPage() {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();

  // Estado dos campos
  const [nomeCompleto, setNomeCompleto] = useState("");
  const [cpf, setCpf] = useState("");
  const [telefone, setTelefone] = useState("");
  const [bio, setBio] = useState("");
  const [instagram, setInstagram] = useState("");
  const [youtube, setYoutube] = useState("");
  const [spotify, setSpotify] = useState("");
  const [tiktok, setTiktok] = useState("");
  const [dataNascimento, setDataNascimento] = useState("");
  const [perfilPublico, setPerfilPublico] = useState(false);
  const [mostrarWhatsapp, setMostrarWhatsapp] = useState(false);
  const [emailContato, setEmailContato] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [endereco, setEndereco] = useState({
    rua: "",
    numero: "",
    bairro: "",
    cidade: "",
    estado: "",
    cep: "",
    tipoResidencia: "casa",
  });
  const [profileLocked, setProfileLocked] = useState(false);

  // Estado de UI
  const [salvando, setSalvando] = useState(false);
  const [mensagem, setMensagem] = useState("");
  const [erro, setErro] = useState("");
  const [isLocked, setIsLocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showAvatarGenerator, setShowAvatarGenerator] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Modo edição por seção
  const [editPessoal, setEditPessoal] = useState(false);
  const [editRedes, setEditRedes] = useState(false);
  const [editEndereco, setEditEndereco] = useState(false);
  const [editPrivacidade, setEditPrivacidade] = useState(false);

  // Buscar dados diretamente do banco
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    async function loadProfile() {
      setLoading(true);
      setErro("");
      const { data, error } = await supabase
        .from("profiles")
        .select("nome, sobrenome, avatar_url, telefone, bio, instagram, youtube, spotify, tiktok, role, payment_info, data_nascimento, perfil_publico, mostrar_whatsapp, endereco, email_contato, cpf, profile_locked")
        .eq("id", user.id)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        setErro("Erro ao carregar perfil: " + error.message);
        setLoading(false);
        return;
      }
      if (data) {
        const locked = !!data.profile_locked;
        setIsLocked(locked);
        setProfileLocked(locked);
        const fullName = [data.nome, data.sobrenome].filter(Boolean).join(" ");
        setNomeCompleto(fullName || data.nome || "");
        setCpf(data.cpf || "");
        setTelefone(data.telefone || "");
        setBio(data.bio || "");
        setInstagram(data.instagram || "");
        setYoutube(data.youtube || "");
        setSpotify(data.spotify || "");
        setTiktok(data.tiktok || "");
        setDataNascimento(data.data_nascimento || "");
        setPerfilPublico(data.perfil_publico || false);
        setMostrarWhatsapp(data.mostrar_whatsapp || false);
        setEmailContato(data.email_contato || user?.email || "");
        setAvatarUrl(data.avatar_url || "");
        if (data.endereco && typeof data.endereco === "object") {
          setEndereco({
            rua: data.endereco.rua || "",
            numero: data.endereco.numero || "",
            bairro: data.endereco.bairro || "",
            cidade: data.endereco.cidade || "",
            estado: data.endereco.estado || "",
            cep: data.endereco.cep || "",
            tipoResidencia: data.endereco.tipoResidencia || "casa",
          });
        } else {
          setEndereco({
            rua: "", numero: "", bairro: "", cidade: "", estado: "", cep: "", tipoResidencia: "casa",
          });
        }
      } else {
        setErro("Perfil não encontrado no banco de dados.");
      }
      setLoading(false);
    }
    loadProfile();
    return () => { cancelled = true; };
  }, [user]);

  function updateEndereco(field: string, value: string) {
    setEndereco((prev) => ({ ...prev, [field]: value }));
  }

  function validarPessoais(): string | null {
    if (!nomeCompleto.trim()) return "Nome completo é obrigatório.";
    if (!cpf.trim() || cpf.replace(/\D/g, "").length < 11) return "CPF válido é obrigatório.";
    if (!dataNascimento.trim()) return "Data de nascimento é obrigatória.";
    if (!telefone.trim() || telefone.replace(/\D/g, "").length < 10) return "Celular é obrigatório.";
    if (!emailContato.trim() || !emailContato.includes("@")) return "E-mail de contato válido é obrigatório.";
    return null;
  }

  async function salvarPessoais() {
    setErro("");
    setMensagem("");
    if (!user) return;
    const validationError = validarPessoais();
    if (validationError) {
      setErro(validationError);
      return;
    }
    setSalvando(true);
    const nameParts = nomeCompleto.trim().split(" ");
    const firstName = nameParts[0] || "";
    const lastName = nameParts.slice(1).join(" ") || null;
    const payload = {
      nome: firstName,
      sobrenome: lastName,
      cpf: cpf.replace(/\D/g, ""),
      telefone: telefone || null,
      bio: bio || null,
      data_nascimento: dataNascimento || null,
      email_contato: emailContato || null,
      avatar_url: avatarUrl || null,
      profile_locked: true,
      updated_at: new Date().toISOString(),
    };
    const { error, data } = await supabase.from("profiles").update(payload).eq("id", user.id).select().single();
    setSalvando(false);
    if (error) {
      setErro("Erro ao salvar dados pessoais: " + error.message);
      return;
    }
    if (!data) {
      setErro("Não foi possível salvar. Verifique se você está logado corretamente.");
      return;
    }
    setIsLocked(true);
    setProfileLocked(true);
    setEditPessoal(false);
    setMensagem("Dados pessoais salvos com sucesso!");
    setTimeout(() => setMensagem(""), 4000);
  }

  async function salvarRedes() {
    setErro("");
    setMensagem("");
    if (!user) return;
    setSalvando(true);
    const { error, data } = await supabase.from("profiles").update({
      instagram: instagram || null,
      youtube: youtube || null,
      spotify: spotify || null,
      tiktok: tiktok || null,
      updated_at: new Date().toISOString(),
    }).eq("id", user.id).select().single();
    setSalvando(false);
    if (error) {
      setErro("Erro ao salvar redes sociais: " + error.message);
      return;
    }
    if (!data) {
      setErro("Não foi possível salvar. Verifique se você está logado corretamente.");
      return;
    }
    setEditRedes(false);
    setMensagem("Redes sociais salvas com sucesso!");
    setTimeout(() => setMensagem(""), 4000);
  }

  async function salvarEndereco() {
    setErro("");
    setMensagem("");
    if (!user) return;
    setSalvando(true);
    const enderecoPayload = Object.values(endereco).some((v) => v && String(v).trim())
      ? {
          rua: endereco.rua || null,
          numero: endereco.numero || null,
          bairro: endereco.bairro || null,
          cidade: endereco.cidade || null,
          estado: endereco.estado || null,
          cep: endereco.cep || null,
          tipoResidencia: endereco.tipoResidencia || "casa",
        }
      : null;
    const { error, data } = await supabase.from("profiles").update({
      endereco: enderecoPayload,
      updated_at: new Date().toISOString(),
    }).eq("id", user.id).select().single();
    setSalvando(false);
    if (error) {
      setErro("Erro ao salvar endereço: " + error.message);
      return;
    }
    if (!data) {
      setErro("Não foi possível salvar. Verifique se você está logado corretamente.");
      return;
    }
    setEditEndereco(false);
    setMensagem("Endereço salvo com sucesso!");
    setTimeout(() => setMensagem(""), 4000);
  }

  async function salvarPrivacidade() {
    setErro("");
    setMensagem("");
    if (!user) return;
    setSalvando(true);
    const { error, data } = await supabase.from("profiles").update({
      perfil_publico: perfilPublico,
      mostrar_whatsapp: mostrarWhatsapp,
      updated_at: new Date().toISOString(),
    }).eq("id", user.id).select().single();
    setSalvando(false);
    if (error) {
      setErro("Erro ao salvar configurações de privacidade: " + error.message);
      return;
    }
    if (!data) {
      setErro("Não foi possível salvar. Verifique se você está logado corretamente.");
      return;
    }
    setEditPrivacidade(false);
    setMensagem("Configurações de privacidade salvas com sucesso!");
    setTimeout(() => setMensagem(""), 4000);
  }

  async function handlePhotoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    setErro("");
    setMensagem("");
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("fileName", file.name);

      const { data, error } = await supabase.functions.invoke('upload-avatar', {
        body: formData,
      });
      if (error || !data) throw new Error(error?.message || data?.error || "Erro no upload da foto");
      if (data.error) throw new Error(data.error);

      const publicUrl = data.url;
      if (!publicUrl) throw new Error("URL pública não retornada pelo servidor");

      setAvatarUrl(publicUrl);
      if (user) {
        await supabase.from("profiles").update({
          avatar_url: publicUrl,
          updated_at: new Date().toISOString(),
        }).eq("id", user.id);
      }
      setMensagem("Foto enviada com sucesso!");
      setTimeout(() => setMensagem(""), 4000);
    } catch (err: any) {
      setErro(err.message || "Erro ao enviar foto.");
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function handleAvatarSelect(url: string) {
    setAvatarUrl(url);
    setShowAvatarGenerator(false);
    if (user) {
      await supabase.from("profiles").update({
        avatar_url: url,
        updated_at: new Date().toISOString(),
      }).eq("id", user.id);
    }
    setMensagem("Avatar selecionado com sucesso!");
    setTimeout(() => setMensagem(""), 4000);
  }

  const displayName = nomeCompleto || "Meu Perfil";
  const idade = calcularIdade(dataNascimento);
  const enderecoFormatado = [
    endereco.rua, endereco.numero, endereco.bairro, endereco.cidade, endereco.estado, endereco.cep,
  ].filter(Boolean).join(", ") || "Nenhum endereço cadastrado";

  if (loading) {
    return (
      <>
        <SEO title="Meu Perfil" noindex />
        <div className="min-h-screen bg-godoy-black">
          <Navbar />
          <div className="pt-32 pb-20 flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-godoy-gold border-t-transparent rounded-full animate-spin" />
          </div>
          <Footer />
        </div>
      </>
    );
  }

  return (
    <>
      <SEO
        title="Meu Perfil"
        description="Edite seu perfil na GodoyProd. Atualize seus dados pessoais, redes sociais, endereço e configurações de privacidade na comunidade."
        keywords="meu perfil, configurações conta, GodoyProd, privacidade perfil"
        noindex
        canonicalPath="/meu-perfil"
      />
      <div className="min-h-screen bg-godoy-black">
        <Navbar />
        <section className="pt-32 pb-20 md:pt-40 md:pb-28">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-5xl mx-auto">
            {/* Header */}
            <div className="mb-10 flex items-center gap-4 flex-wrap">
              <div className="relative flex-shrink-0">
                <button
                  onClick={() => setShowAvatarGenerator(true)}
                  className="relative w-20 h-20 rounded-full overflow-hidden border-2 border-godoy-gold/40 hover:border-godoy-gold transition-colors flex-shrink-0 group"
                >
                  {avatarUrl ? (
                    <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-godoy-charcoal flex items-center justify-center">
                      <i className="ri-user-line text-3xl text-godoy-lightgray" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <i className="ri-sparkling-line text-white text-lg" />
                  </div>
                </button>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute -bottom-1 -right-1 w-7 h-7 bg-godoy-gold rounded-full flex items-center justify-center hover:bg-godoy-goldlight transition-colors shadow-md"
                  title="Enviar foto própria"
                >
                  {uploadingPhoto ? (
                    <div className="w-3 h-3 border border-godoy-black border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <i className="ri-camera-line text-godoy-black text-xs" />
                  )}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={handlePhotoUpload}
                />
              </div>
              <div>
                <h1 className="font-serif text-3xl md:text-4xl font-bold text-godoy-cream">
                  {displayName}
                </h1>
                <p className="text-godoy-lightgray text-sm mt-1">{user?.email}</p>
                {perfilPublico && (
                  <span className="inline-flex items-center gap-1 mt-1.5 text-xs text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded">
                    <i className="ri-earth-line" /> Perfil público na comunidade
                  </span>
                )}
              </div>
              <div className="ml-auto flex items-center gap-2 flex-wrap">
                <Link
                  to="/minha-conta"
                  className="flex items-center gap-2 px-4 py-2 bg-godoy-charcoal/60 border border-godoy-gray/30 rounded-md text-sm text-godoy-cream hover:border-godoy-gold/40 hover:text-godoy-gold transition-all"
                >
                  <i className="ri-book-open-line" />
                  Minhas Partituras
                </Link>
                <Link
                  to="/minha-playlist"
                  className="flex items-center gap-2 px-4 py-2 bg-godoy-charcoal/60 border border-godoy-gray/30 rounded-md text-sm text-godoy-cream hover:border-godoy-gold/40 hover:text-godoy-gold transition-all"
                >
                  <i className="ri-play-list-line" />
                  Minha Playlist
                </Link>
                {isAdmin && (
                  <Link
                    to="/admin/partituras"
                    className="flex items-center gap-2 px-4 py-2 bg-godoy-gold/10 border border-godoy-gold/40 rounded-md text-sm text-godoy-gold hover:bg-godoy-gold/20 transition-all"
                  >
                    <i className="ri-shield-user-line" />
                    Painel Admin
                  </Link>
                )}
              </div>
            </div>

            {mensagem && (
              <div className="mb-6 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-md text-emerald-400 text-sm">
                {mensagem}
              </div>
            )}
            {erro && (
              <div className="mb-6 p-3 bg-red-500/10 border border-red-500/30 rounded-md text-red-400 text-sm">
                {erro}
              </div>
            )}

            {/* === DADOS PESSOAIS === */}
            <div className="bg-godoy-charcoal/60 border border-godoy-gray/20 rounded-lg p-6 md:p-8 mb-8">
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-serif text-lg font-semibold text-godoy-gold flex items-center gap-2">
                  <i className="ri-user-settings-line" />
                  Dados Pessoais
                  {isLocked && (
                    <span className="text-xs font-normal text-godoy-lightgray/60 ml-2 flex items-center gap-1">
                      <i className="ri-lock-line" /> Travado
                    </span>
                  )}
                </h2>
                {!editPessoal && (
                  <button
                    onClick={() => setEditPessoal(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-godoy-gold/10 border border-godoy-gold/30 rounded-md text-xs text-godoy-gold hover:bg-godoy-gold/20 transition-all whitespace-nowrap"
                  >
                    <i className="ri-edit-line" /> Editar
                  </button>
                )}
              </div>

              {editPessoal ? (
                <div className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="md:col-span-2">
                      <label className="block text-sm text-godoy-lightgray mb-1.5">
                        Nome Completo <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={nomeCompleto}
                        onChange={(e) => setNomeCompleto(e.target.value)}
                        disabled={isLocked}
                        placeholder="Seu nome completo"
                        className={`w-full px-4 py-2.5 border rounded-md text-sm ${
                          isLocked
                            ? "bg-godoy-black/40 border-godoy-gray/20 text-godoy-lightgray/60 cursor-not-allowed"
                            : "bg-godoy-black/60 border-godoy-gray/30 text-godoy-cream focus:outline-none focus:border-godoy-gold/50"
                        }`}
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-godoy-lightgray mb-1.5">
                        CPF <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={cpf}
                        onChange={(e) => setCpf(formatCpf(e.target.value))}
                        disabled={isLocked}
                        placeholder="000.000.000-00"
                        maxLength={14}
                        className={`w-full px-4 py-2.5 border rounded-md text-sm ${
                          isLocked
                            ? "bg-godoy-black/40 border-godoy-gray/20 text-godoy-lightgray/60 cursor-not-allowed"
                            : "bg-godoy-black/60 border-godoy-gray/30 text-godoy-cream focus:outline-none focus:border-godoy-gold/50"
                        }`}
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-godoy-lightgray mb-1.5">
                        Data de Nascimento <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="date"
                        required
                        value={dataNascimento}
                        onChange={(e) => setDataNascimento(e.target.value)}
                        disabled={isLocked}
                        className={`w-full px-4 py-2.5 border rounded-md text-sm ${
                          isLocked
                            ? "bg-godoy-black/40 border-godoy-gray/20 text-godoy-lightgray/60 cursor-not-allowed"
                            : "bg-godoy-black/60 border-godoy-gray/30 text-godoy-cream focus:outline-none focus:border-godoy-gold/50"
                        }`}
                      />
                      {idade !== null && (
                        <p className="text-godoy-lightgray/50 text-xs mt-1">{idade} anos</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm text-godoy-lightgray mb-1.5">
                        Celular / WhatsApp <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="tel"
                        required
                        value={telefone}
                        onChange={(e) => setTelefone(formatPhone(e.target.value))}
                        disabled={isLocked}
                        placeholder="(15) 9 9181-1212"
                        className={`w-full px-4 py-2.5 border rounded-md text-sm ${
                          isLocked
                            ? "bg-godoy-black/40 border-godoy-gray/20 text-godoy-lightgray/60 cursor-not-allowed"
                            : "bg-godoy-black/60 border-godoy-gray/30 text-godoy-cream focus:outline-none focus:border-godoy-gold/50"
                        }`}
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-godoy-lightgray mb-1.5">
                        E-mail de Contato <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="email"
                        required
                        value={emailContato}
                        onChange={(e) => setEmailContato(e.target.value)}
                        disabled={isLocked}
                        placeholder="seu@email.com"
                        className={`w-full px-4 py-2.5 border rounded-md text-sm ${
                          isLocked
                            ? "bg-godoy-black/40 border-godoy-gray/20 text-godoy-lightgray/60 cursor-not-allowed"
                            : "bg-godoy-black/60 border-godoy-gray/30 text-godoy-cream focus:outline-none focus:border-godoy-gold/50"
                        }`}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm text-godoy-lightgray mb-1.5">Biografia</label>
                      <textarea
                        value={bio}
                        onChange={(e) => setBio(e.target.value)}
                        rows={3}
                        placeholder="Conte um pouco sobre você, seu ministério, sua história com a música..."
                        className="w-full px-4 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream placeholder:text-godoy-lightgray/40 focus:outline-none focus:border-godoy-gold/50 text-sm resize-none"
                      />
                    </div>
                  </div>

                  {isLocked && (
                    <div className="p-3 bg-godoy-black/40 border border-godoy-gray/20 rounded-md">
                      <p className="text-godoy-lightgray/70 text-xs flex items-start gap-2">
                        <i className="ri-customer-service-2-line text-godoy-gold mt-0.5 flex-shrink-0" />
                        <span>
                          Para alterar dados pessoais (CPF, Data de Nascimento, Celular ou E-mail), entre em contato com o{" "}
                          <a href="https://wa.me/5515991811212" target="_blank" rel="noopener noreferrer" className="text-godoy-gold hover:underline">
                            suporte técnico
                          </a>.
                        </span>
                      </p>
                    </div>
                  )}

                  <div className="flex items-center gap-3">
                    <button
                      onClick={salvarPessoais}
                      disabled={salvando}
                      className="px-6 py-2.5 bg-godoy-gold text-godoy-black font-semibold rounded-md hover:bg-godoy-goldlight transition-all text-sm disabled:opacity-60 whitespace-nowrap"
                    >
                      {salvando ? "Salvando..." : "Salvar Dados Pessoais"}
                    </button>
                    <button
                      onClick={() => setEditPessoal(false)}
                      className="px-6 py-2.5 border border-godoy-gray/40 text-godoy-cream rounded-md hover:border-godoy-gold/40 hover:text-godoy-gold transition-all text-sm whitespace-nowrap"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InfoCard label="Nome Completo" value={nomeCompleto || "Não informado"} />
                    <InfoCard label="CPF" value={formatCpfDisplay(cpf) || "Não informado"} />
                    <InfoCard label="Data de Nascimento" value={formatDateBR(dataNascimento) || "Não informada"} />
                    <InfoCard label="Celular / WhatsApp" value={formatPhoneDisplay(telefone) || "Não informado"} />
                    <InfoCard label="E-mail de Contato" value={emailContato || "Não informado"} />
                    <InfoCard label="Idade" value={idade !== null ? `${idade} anos` : "Não informada"} />
                  </div>
                  {bio && (
                    <div className="mt-4 p-4 bg-godoy-black/40 border border-godoy-gray/20 rounded-md">
                      <p className="text-xs text-godoy-lightgray/60 mb-1">Biografia</p>
                      <p className="text-sm text-godoy-cream whitespace-pre-wrap">{bio}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* === REDES SOCIAIS === */}
            <div className="bg-godoy-charcoal/60 border border-godoy-gray/20 rounded-lg p-6 md:p-8 mb-8">
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-serif text-lg font-semibold text-godoy-gold flex items-center gap-2">
                  <i className="ri-share-box-line" />
                  Redes Sociais & Links
                </h2>
                {!editRedes && (
                  <button
                    onClick={() => setEditRedes(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-godoy-gold/10 border border-godoy-gold/30 rounded-md text-xs text-godoy-gold hover:bg-godoy-gold/20 transition-all whitespace-nowrap"
                  >
                    <i className="ri-edit-line" /> Editar
                  </button>
                )}
              </div>

              {editRedes ? (
                <div className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm text-godoy-lightgray mb-1.5">Instagram</label>
                      <input
                        type="url"
                        value={instagram}
                        onChange={(e) => setInstagram(e.target.value)}
                        placeholder="https://instagram.com/seuusuario"
                        className="w-full px-4 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream placeholder:text-godoy-lightgray/40 focus:outline-none focus:border-godoy-gold/50 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-godoy-lightgray mb-1.5">YouTube</label>
                      <input
                        type="url"
                        value={youtube}
                        onChange={(e) => setYoutube(e.target.value)}
                        placeholder="https://youtube.com/@seucanal"
                        className="w-full px-4 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream placeholder:text-godoy-lightgray/40 focus:outline-none focus:border-godoy-gold/50 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-godoy-lightgray mb-1.5">Spotify</label>
                      <input
                        type="url"
                        value={spotify}
                        onChange={(e) => setSpotify(e.target.value)}
                        placeholder="https://open.spotify.com/artist/..."
                        className="w-full px-4 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream placeholder:text-godoy-lightgray/40 focus:outline-none focus:border-godoy-gold/50 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-godoy-lightgray mb-1.5">TikTok</label>
                      <input
                        type="url"
                        value={tiktok}
                        onChange={(e) => setTiktok(e.target.value)}
                        placeholder="https://tiktok.com/@seuusuario"
                        className="w-full px-4 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream placeholder:text-godoy-lightgray/40 focus:outline-none focus:border-godoy-gold/50 text-sm"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={salvarRedes}
                      disabled={salvando}
                      className="px-6 py-2.5 bg-godoy-gold text-godoy-black font-semibold rounded-md hover:bg-godoy-goldlight transition-all text-sm disabled:opacity-60 whitespace-nowrap"
                    >
                      {salvando ? "Salvando..." : "Salvar Redes Sociais"}
                    </button>
                    <button
                      onClick={() => setEditRedes(false)}
                      className="px-6 py-2.5 border border-godoy-gray/40 text-godoy-cream rounded-md hover:border-godoy-gold/40 hover:text-godoy-gold transition-all text-sm whitespace-nowrap"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <SocialCard icon="ri-instagram-line" label="Instagram" value={instagram} color="text-pink-400" />
                  <SocialCard icon="ri-youtube-line" label="YouTube" value={youtube} color="text-red-400" />
                  <SocialCard icon="ri-spotify-line" label="Spotify" value={spotify} color="text-green-400" />
                  <SocialCard icon="ri-tiktok-line" label="TikTok" value={tiktok} color="text-godoy-cream" />
                </div>
              )}
            </div>

            {/* === ENDEREÇO === */}
            <div className="bg-godoy-charcoal/60 border border-godoy-gray/20 rounded-lg p-6 md:p-8 mb-8">
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-serif text-lg font-semibold text-godoy-gold flex items-center gap-2">
                  <i className="ri-map-pin-line" />
                  Endereço para Entrega / Faturamento
                  <span className="text-xs font-normal text-emerald-400 ml-2 flex items-center gap-1">
                    <i className="ri-lock-line" /> Somente você vê
                  </span>
                </h2>
                {!editEndereco && (
                  <button
                    onClick={() => setEditEndereco(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-godoy-gold/10 border border-godoy-gold/30 rounded-md text-xs text-godoy-gold hover:bg-godoy-gold/20 transition-all whitespace-nowrap"
                  >
                    <i className="ri-edit-line" /> Editar
                  </button>
                )}
              </div>

              {editEndereco ? (
                <div className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="md:col-span-2">
                      <label className="block text-sm text-godoy-lightgray mb-1.5">Rua / Avenida</label>
                      <input
                        type="text"
                        value={endereco.rua}
                        onChange={(e) => updateEndereco("rua", e.target.value)}
                        placeholder="Rua das Flores"
                        className="w-full px-4 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream placeholder:text-godoy-lightgray/40 focus:outline-none focus:border-godoy-gold/50 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-godoy-lightgray mb-1.5">Número</label>
                      <input
                        type="text"
                        value={endereco.numero}
                        onChange={(e) => updateEndereco("numero", e.target.value)}
                        placeholder="123"
                        className="w-full px-4 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream placeholder:text-godoy-lightgray/40 focus:outline-none focus:border-godoy-gold/50 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-godoy-lightgray mb-1.5">Bairro</label>
                      <input
                        type="text"
                        value={endereco.bairro}
                        onChange={(e) => updateEndereco("bairro", e.target.value)}
                        placeholder="Centro"
                        className="w-full px-4 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream placeholder:text-godoy-lightgray/40 focus:outline-none focus:border-godoy-gold/50 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-godoy-lightgray mb-1.5">Cidade</label>
                      <input
                        type="text"
                        value={endereco.cidade}
                        onChange={(e) => updateEndereco("cidade", e.target.value)}
                        placeholder="São Paulo"
                        className="w-full px-4 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream placeholder:text-godoy-lightgray/40 focus:outline-none focus:border-godoy-gold/50 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-godoy-lightgray mb-1.5">Estado</label>
                      <select
                        value={endereco.estado}
                        onChange={(e) => updateEndereco("estado", e.target.value)}
                        className="w-full px-4 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream focus:outline-none focus:border-godoy-gold/50 text-sm"
                      >
                        <option value="">Selecione...</option>
                        {["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"].map((uf) => (
                          <option key={uf} value={uf}>{uf}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-godoy-lightgray mb-1.5">CEP</label>
                      <input
                        type="text"
                        value={endereco.cep}
                        onChange={(e) => updateEndereco("cep", e.target.value)}
                        placeholder="00000-000"
                        className="w-full px-4 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream placeholder:text-godoy-lightgray/40 focus:outline-none focus:border-godoy-gold/50 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-godoy-lightgray mb-1.5">Tipo de Residência</label>
                      <select
                        value={endereco.tipoResidencia}
                        onChange={(e) => updateEndereco("tipoResidencia", e.target.value)}
                        className="w-full px-4 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream focus:outline-none focus:border-godoy-gold/50 text-sm"
                      >
                        <option value="casa">Casa</option>
                        <option value="apartamento">Apartamento</option>
                        <option value="condominio">Condomínio</option>
                        <option value="chacara">Chácara / Sítio</option>
                        <option value="outro">Outro</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={salvarEndereco}
                      disabled={salvando}
                      className="px-6 py-2.5 bg-godoy-gold text-godoy-black font-semibold rounded-md hover:bg-godoy-goldlight transition-all text-sm disabled:opacity-60 whitespace-nowrap"
                    >
                      {salvando ? "Salvando..." : "Salvar Endereço"}
                    </button>
                    <button
                      onClick={() => setEditEndereco(false)}
                      className="px-6 py-2.5 border border-godoy-gray/40 text-godoy-cream rounded-md hover:border-godoy-gold/40 hover:text-godoy-gold transition-all text-sm whitespace-nowrap"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-godoy-black/40 border border-godoy-gray/20 rounded-md">
                  <p className="text-sm text-godoy-cream">{enderecoFormatado}</p>
                  {endereco.tipoResidencia && enderecoFormatado !== "Nenhum endereço cadastrado" && (
                    <p className="text-xs text-godoy-lightgray/50 mt-1 capitalize">{endereco.tipoResidencia}</p>
                  )}
                </div>
              )}
            </div>

            {/* === PRIVACIDADE === */}
            <div className="bg-godoy-charcoal/60 border border-godoy-gray/20 rounded-lg p-6 md:p-8 mb-8">
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-serif text-lg font-semibold text-godoy-gold flex items-center gap-2">
                  <i className="ri-shield-user-line" />
                  Privacidade & Comunidade GodoyProd
                </h2>
                {!editPrivacidade && (
                  <button
                    onClick={() => setEditPrivacidade(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-godoy-gold/10 border border-godoy-gold/30 rounded-md text-xs text-godoy-gold hover:bg-godoy-gold/20 transition-all whitespace-nowrap"
                  >
                    <i className="ri-edit-line" /> Editar
                  </button>
                )}
              </div>

              {editPrivacidade ? (
                <div className="space-y-5">
                  <p className="text-godoy-lightgray text-sm mb-3">
                    Faça parte da nossa comunidade! Quando seu perfil é público, outros músicos podem te encontrar,
                    conhecer seu trabalho e se conectar pelas redes sociais. Seu <strong>endereço nunca é exibido</strong>.
                  </p>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={perfilPublico}
                      onChange={(e) => setPerfilPublico(e.target.checked)}
                      className="mt-1 w-4 h-4 accent-godoy-gold rounded border-godoy-gray/30 bg-godoy-black/60"
                    />
                    <div>
                      <p className="text-godoy-cream text-sm font-medium">Quero deixar meu perfil público na comunidade</p>
                      <p className="text-godoy-lightgray/70 text-xs mt-0.5">
                        Outros usuários poderão ver seu nome, foto, biografia, idade e links das redes sociais no seu perfil público.
                      </p>
                    </div>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={mostrarWhatsapp}
                      onChange={(e) => setMostrarWhatsapp(e.target.checked)}
                      className="mt-1 w-4 h-4 accent-godoy-gold rounded border-godoy-gray/30 bg-godoy-black/60"
                    />
                    <div>
                      <p className="text-godoy-cream text-sm font-medium">Exibir meu WhatsApp no perfil público</p>
                      <p className="text-godoy-lightgray/70 text-xs mt-0.5">
                        Apenas visível se seu perfil for público. Outros músicos poderão te contatar diretamente pelo WhatsApp.
                      </p>
                    </div>
                  </label>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={salvarPrivacidade}
                      disabled={salvando}
                      className="px-6 py-2.5 bg-godoy-gold text-godoy-black font-semibold rounded-md hover:bg-godoy-goldlight transition-all text-sm disabled:opacity-60 whitespace-nowrap"
                    >
                      {salvando ? "Salvando..." : "Salvar Configurações"}
                    </button>
                    <button
                      onClick={() => setEditPrivacidade(false)}
                      className="px-6 py-2.5 border border-godoy-gray/40 text-godoy-cream rounded-md hover:border-godoy-gold/40 hover:text-godoy-gold transition-all text-sm whitespace-nowrap"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-godoy-black/40 border border-godoy-gray/20 rounded-md">
                    <i className={`${perfilPublico ? "ri-eye-line text-emerald-400" : "ri-eye-off-line text-godoy-lightgray/50"} text-lg`} />
                    <div>
                      <p className="text-sm text-godoy-cream">Perfil público na comunidade</p>
                      <p className="text-xs text-godoy-lightgray/60">{perfilPublico ? "Ativo — outros usuários podem ver seu perfil" : "Inativo — seu perfil fica privado"}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-godoy-black/40 border border-godoy-gray/20 rounded-md">
                    <i className={`${mostrarWhatsapp ? "ri-whatsapp-line text-emerald-400" : "ri-whatsapp-line text-godoy-lightgray/50"} text-lg`} />
                    <div>
                      <p className="text-sm text-godoy-cream">Exibir WhatsApp no perfil</p>
                      <p className="text-xs text-godoy-lightgray/60">{mostrarWhatsapp ? "Ativo — número visível no perfil público" : "Inativo — número oculto"}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
        <Footer />
        {showAvatarGenerator && (
          <AvatarGenerator
            onSelect={handleAvatarSelect}
            onClose={() => setShowAvatarGenerator(false)}
          />
        )}
      </div>
    </>
  );
}

function InfoCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="p-3 bg-godoy-black/40 border border-godoy-gray/20 rounded-md">
      <p className="text-xs text-godoy-lightgray/60 mb-1">{label}</p>
      <p className="text-sm text-godoy-cream font-medium">{value}</p>
    </div>
  );
}

function SocialCard({ icon, label, value, color }: { icon: string; label: string; value: string; color: string }) {
  if (!value) {
    return (
      <div className="flex items-center gap-3 p-3 bg-godoy-black/40 border border-godoy-gray/20 rounded-md">
        <i className={`${icon} text-lg text-godoy-lightgray/30`} />
        <div>
          <p className="text-sm text-godoy-cream">{label}</p>
          <p className="text-xs text-godoy-lightgray/40">Não informado</p>
        </div>
      </div>
    );
  }
  return (
    <a
      href={value}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-3 p-3 bg-godoy-black/40 border border-godoy-gray/20 rounded-md hover:border-godoy-gold/40 transition-all group"
    >
      <i className={`${icon} text-lg ${color} group-hover:scale-110 transition-transform`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-godoy-cream">{label}</p>
        <p className="text-xs text-godoy-lightgray/50 truncate">{value}</p>
      </div>
      <i className="ri-external-link-line text-godoy-lightgray/30 text-xs" />
    </a>
  );
}